import { Seo } from "@/components/Seo";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, UploadCloud, X, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

const inputCls = "w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/40 text-ink placeholder:text-inkfaint";

const TRIAL_PLAN_KEYS = ["student_1m", "advocate_1m", "client_1m"];
const isTrialPlan = (key) => TRIAL_PLAN_KEYS.includes(key);

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [verifyPlan, setVerifyPlan] = useState(null);
  const [collegeName, setCollegeName] = useState("");
  const [collegeFile, setCollegeFile] = useState(null);
  const [barId, setBarId] = useState("");
  const [barFile, setBarFile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setCycles(data.cycles || [])).catch(() => {});
  }, []);

  const proceedPayment = async (planKey) => {
    setBusy(planKey);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway.");
      const { data } = await api.post("/payments/create-order", { plan: planKey });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "JusticeVerse",
        description: `${data.plan_name} Plan — ${data.cycle_label}`,
        order_id: data.order_id,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: planKey,
            });
            await refreshUser();
            toast.success(`Subscribed to ${data.plan_name}!`);
            navigate("/dashboard");
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed.");
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const proceedTrial = async (planKey) => {
    setBusy(planKey);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load payment gateway.");
      const { data } = await api.post("/payments/create-subscription", { plan: planKey });
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "JusticeVerse",
        description: data.is_trial
          ? `${data.plan_name} — 1-Month Free Trial (autopay after 30 days)`
          : `${data.plan_name} — Monthly Autopay`,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify-subscription", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
              plan: planKey,
            });
            await refreshUser();
            toast.success(data.is_trial ? "Your 1-month free trial has started — no charge today!" : `Subscribed to ${data.plan_name}!`);
            navigate("/dashboard");
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed.");
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const proceed = (planKey) => (isTrialPlan(planKey) ? proceedTrial(planKey) : proceedPayment(planKey));

  const openSubscribe = (plan) => {
    if (!user) {
      toast.error("Sign in to start your free trial.");
      navigate("/login");
      return;
    }
    if (plan.verify === "student" || plan.verify === "advocate") {
      setCollegeName(""); setCollegeFile(null); setBarId(""); setBarFile(null);
      setVerifyPlan(plan);
      return;
    }
    proceed(plan.key);
  };

  const submitVerification = async () => {
    setVerifying(true);
    try {
      const fd = new FormData();
      if (verifyPlan.verify === "student") {
        if (!collegeName.trim() || !collegeFile) {
          toast.error("Enter your college name and upload your College ID photo.");
          setVerifying(false);
          return;
        }
        fd.append("college_name", collegeName);
        fd.append("college_id_photo", collegeFile);
        await api.post("/verify/student", fd);
      } else {
        if (!barId.trim()) {
          toast.error("Enter your BAR Council ID / enrolment number.");
          setVerifying(false);
          return;
        }
        fd.append("bar_council_id", barId);
        if (barFile) fd.append("bar_council_photo", barFile);
        await api.post("/verify/advocate", fd);
      }
      const planKey = verifyPlan.key;
      setVerifyPlan(null);
      toast.success("Verification submitted. Proceeding…");
      await proceed(planKey);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setVerifying(false);
    }
  };

  const cycle = cycles.find((c) => c.key === activeCycle);
  const plans = cycle?.plans || [];
  const gridCols = plans.length <= 1 ? "max-w-md mx-auto"
    : plans.length <= 3 ? "lg:grid-cols-3 md:grid-cols-2"
    : plans.length === 4 ? "lg:grid-cols-4 md:grid-cols-2"
    : "lg:grid-cols-5 md:grid-cols-2";

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid="pricing-page">
      <Seo title="Pricing — JusticeVerse Plans from ₹29/month" description="Affordable JusticeVerse plans for students, advocates, clients, law firms and institutions. Monthly, 3-month and yearly options with CaseBrief AI included." />
      <div className="text-center max-w-2xl mx-auto">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Membership</span>
        <h1 className="font-serif text-4xl sm:text-6xl font-light text-ink mt-4">Choose your chamber</h1>
        <p className="text-inkmuted mt-5">Transparent pricing for students, advocates, clients & firms. Cancel anytime.</p>
      </div>

      {/* Billing cycle toggle */}
      <div className="flex justify-center mt-10">
        <div className="inline-flex items-center gap-1 glass-card rounded-full p-1.5" data-testid="cycle-toggle">
          {cycles.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCycle(c.key)}
              data-testid={`cycle-${c.key}`}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeCycle === c.key ? "bg-gold text-black" : "text-inkmuted hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid gap-5 mt-12 ${gridCols}`}>
        {plans.map((p) => {
          const featured = !!p.featured;
          const current = user?.plan === p.key;
          return (
            <div
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              className={`glass-card rounded-3xl p-7 flex flex-col relative ${featured ? "border-gold/50 shadow-[0_0_40px_rgb(var(--c-gold)/0.12)]" : ""}`}
            >
              {featured && (
                <span className={`absolute -top-3 font-mono text-[10px] uppercase tracking-[0.2em] bg-gold text-black px-4 py-1 rounded-full whitespace-nowrap ${
                  p.discount ? "right-4" : "left-1/2 -translate-x-1/2"
                }`}>
                  Most Popular
                </span>
              )}
              {p.discount && (
                <span
                  data-testid={`discount-badge-${p.key}`}
                  className="absolute -top-3 left-4 font-mono text-[10px] uppercase tracking-[0.15em] bg-emerald-500 text-btnink px-3 py-1 rounded-full whitespace-nowrap shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                >
                  {p.discount}% OFF
                </span>
              )}
              <h3 className="font-serif text-2xl text-ink">{p.name}</h3>
              <p className="text-xs text-inkfaint mt-2 min-h-[40px]">{p.tagline}</p>
              <div className="mt-5 flex items-end gap-2 flex-wrap">
                {p.original_amount && p.original_amount > p.amount && (
                  <span className="font-serif text-xl text-inkfaint line-through" data-testid={`original-price-${p.key}`}>
                    ₹{(p.original_amount / 100).toLocaleString("en-IN")}
                  </span>
                )}
                <span className="font-serif text-4xl text-ink" data-testid={`price-${p.key}`}>
                  ₹{(p.amount / 100).toLocaleString("en-IN")}
                </span>
                <span className="text-inkfaint text-xs mb-1.5">{cycle?.suffix}</span>
              </div>
              <ul className="space-y-2.5 mt-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-inkmuted">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openSubscribe(p)}
                disabled={busy === p.key || current}
                data-testid={`subscribe-btn-${p.key}`}
                className={`mt-7 rounded-full px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                  featured ? "bg-btn text-btnink hover:bg-btnhover" : "bg-surface border border-hairline text-ink hover:bg-surface2"
                } disabled:opacity-50`}
              >
                {busy === p.key && <Loader2 className="w-4 h-4 animate-spin" />}
                {current
                  ? "Current Plan"
                  : isTrialPlan(p.key) && !user?.trial_used
                  ? "Start 1-Month Free Trial"
                  : "Subscribe"}
              </button>
              {isTrialPlan(p.key) && !current && !user?.trial_used && (
                <p className="text-center font-mono text-[10px] text-gold/90 mt-2" data-testid={`trial-note-${p.key}`}>
                  ₹0 today · autopay ₹{(p.amount / 100).toLocaleString("en-IN")}/mo after 30 days · cancel anytime
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-center font-mono text-xs text-inkfaint mt-10">
        Payments secured by Razorpay
      </p>

      {verifyPlan && (
        <div className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !verifying && setVerifyPlan(null)} data-testid="verify-modal">
          <div className="glass-card rounded-3xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-gold" />
                <h3 className="font-serif text-2xl text-ink">
                  {verifyPlan.verify === "student" ? "Student Verification" : "Advocate Verification"}
                </h3>
              </div>
              <button onClick={() => !verifying && setVerifyPlan(null)} className="text-inkmuted hover:text-ink"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-inkfaint mt-2">
              {verifyPlan.verify === "student"
                ? "Upload your College ID to unlock the discounted Student plan."
                : "Provide your BAR Council ID / enrolment number to verify your practice."}
            </p>

            <div className="space-y-4 mt-6">
              {verifyPlan.verify === "student" ? (
                <>
                  <input value={collegeName} onChange={(e) => setCollegeName(e.target.value)} data-testid="college-name" placeholder="College / University name" className={inputCls} />
                  <label className="border border-dashed border-hairline rounded-2xl p-5 flex flex-col items-center cursor-pointer hover:border-gold/40 transition-all" data-testid="college-id-dropzone">
                    <UploadCloud className="w-6 h-6 text-gold" />
                    <span className="text-sm text-inkmuted mt-2 text-center">{collegeFile ? collegeFile.name : "Upload College ID photo (JPG / PNG / PDF)"}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" data-testid="college-id-input" onChange={(e) => setCollegeFile(e.target.files?.[0] || null)} />
                  </label>
                </>
              ) : (
                <>
                  <input value={barId} onChange={(e) => setBarId(e.target.value)} data-testid="bar-id" placeholder="BAR Council ID / Enrolment No. (e.g. D/1234/2020)" className={inputCls} />
                  <label className="border border-dashed border-hairline rounded-2xl p-5 flex flex-col items-center cursor-pointer hover:border-gold/40 transition-all" data-testid="bar-photo-dropzone">
                    <UploadCloud className="w-6 h-6 text-gold" />
                    <span className="text-sm text-inkmuted mt-2 text-center">{barFile ? barFile.name : "Upload BAR Council ID photo (optional)"}</span>
                    <input type="file" accept="image/*,.pdf" className="hidden" data-testid="bar-photo-input" onChange={(e) => setBarFile(e.target.files?.[0] || null)} />
                  </label>
                </>
              )}
              <button onClick={submitVerification} disabled={verifying} data-testid="submit-verification-btn" className="w-full rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2">
                {verifying && <Loader2 className="w-4 h-4 animate-spin" />} Verify &amp; Continue
              </button>
              <p className="font-mono text-[10px] text-inkfaint text-center">Your document is stored securely for verification only.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
